import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { InterventionActions } from '../../../interventions/store/actions/interventions';
import { useToaster } from '../../../shared/composables/use-toaster';
import { getOwnerFullName } from '../../../site-owner/utils/owner.util';
import { DeviceActions } from '../actions/device.actions';
import { SiteDetailPageActions } from '../actions/site-detail-page.actions';
import { getSelectedDevice } from '../selectors/device-detail';
import { getSiteOwner } from '../selectors/site';
import { getUISiteId } from '../selectors/ui';

@Injectable({ providedIn: 'root' })
export class NotificationsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);
    private readonly toaster = useToaster();

    notifySendingInterventionRequestSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(InterventionActions.interventionRequestSubmitted),
                tap(() =>
                    this.toaster.success({
                        title: this.translateService.instant('TOASTER_INTERVENTION_REQUEST_SENT_MESSAGE'),
                    }),
                ),
            ),
        { dispatch: false },
    );

    notifySendingInterventionRequestFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(InterventionActions.interventionRequestFailed),
                tap(() =>
                    this.toaster.error({
                        title: this.translateService.instant('ERROR_WHILE_OPENING_NEW_INTERVENTION'),
                        duration: 5 * 1000,
                    }),
                ),
            ),
        { dispatch: false },
    );

    notifyUserWhenSectionRefreshFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    SiteDetailPageActions.settingsRefreshFailed,
                    SiteDetailPageActions.diagnosticSectionRefreshFailed,
                ),
                withLatestFrom(this.store.select(getSelectedDevice), this.store.select(getUISiteId)),
                filter(([, device]) => device != null),
                switchMap(([{ sectionTitle }, device, siteId]) =>
                    this.store.select(getSiteOwner(siteId)).pipe(
                        take(1),
                        tap((owner) =>
                            this.toaster.error({
                                title: device.rawData.name,
                                subtitle: this.translateService.instant('EQUIPMENT_REFRESH_TOAST_ERROR', {
                                    sectionLabel: this.translateService.instant(sectionTitle),
                                }),
                                message: getOwnerFullName(owner),
                            }),
                        ),
                    ),
                ),
            ),
        { dispatch: false },
    );

    notifyUserWhenSectionRefreshCompleted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteDetailPageActions.sectionRefreshCompleted),
                tap(() =>
                    this.toaster.success({
                        title: this.translateService.instant('EQUIPMENT_DETAIL_STATE_SECTION_UPDATE_SUCCESS_TITLE'),
                        message: this.translateService.instant('EQUIPMENT_DETAIL_STATE_SECTION_UPDATE_SUCCESS_MESSAGE'),
                    }),
                ),
            ),
        { dispatch: false },
    );

    notifyUserWhenDeviceUpdateFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DeviceActions.deviceUpdateFailed),
                mergeMap((updateCommand) =>
                    this.store.select(getSiteOwner(updateCommand.device.site_id)).pipe(
                        take(1),
                        tap((owner) =>
                            this.toaster.error({
                                title: updateCommand.device.name,
                                subtitle: this.translateService.instant('TOASTER_DEVICE_UPSATE_STATE_INVALID', {
                                    stateLabel: this.translateService.instant(updateCommand.property.label),
                                }),
                                message: getOwnerFullName(owner),
                            }),
                        ),
                    ),
                ),
            ),
        { dispatch: false },
    );
}
